import { type MetaFunction } from '@remix-run/node'

export const meta: MetaFunction = () => [
	{ title: 'Privacy Policy - MinHub' },
	{ name: 'description', content: 'Privacy Policy for MinHub - We do not sell your information' },
]

export default function PrivacyRoute() {
	return (
		<div className="mx-auto max-w-4xl px-4 py-16">
			<h1 className="mb-8 text-4xl font-bold">Privacy Policy</h1>
			
			<div className="space-y-6 text-base leading-7">
				<p className="text-muted-foreground mb-6">
					Last updated: January 12, 2026
				</p>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
					<p className="mb-4">
						MinHub ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
					<p className="mb-4">We collect information that you provide directly to us, including:</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>Account information (name, email address, password)</li>
						<li>Profile information you choose to provide</li>
						<li>Content you create, upload, or share through the Service</li>
						<li>Communications with us (support requests, feedback)</li>
						<li>Payment information (processed securely through third-party payment processors)</li>
					</ul>
					<p className="mb-4">We also automatically collect certain information when you use our Service:</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>Usage data (how you interact with the Service)</li>
						<li>Device information (IP address, browser type, operating system)</li>
						<li>Log data (access times, pages viewed)</li>
					</ul>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
					<p className="mb-4">We use the information we collect to:</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>Provide, maintain, and improve our Service</li>
						<li>Process transactions and send related information</li>
						<li>Send you technical notices, updates, and support messages</li>
						<li>Respond to your comments, questions, and requests</li>
						<li>Monitor and analyze trends, usage, and activities</li>
						<li>Detect, prevent, and address technical issues and security threats</li>
					</ul>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">4. We Do Not Sell Your Information</h2>
					<p className="mb-4 font-semibold text-lg">
						We do not sell, rent, or trade your personal information to third parties. Your data is yours, and we respect your privacy.
					</p>
					<p className="mb-4">
						We may share your information only in the following limited circumstances:
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>Service Providers:</strong> We may share information with trusted service providers who assist us in operating our Service, conducting our business, or serving our users, so long as those parties agree to keep this information confidential.</li>
						<li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
						<li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
						<li><strong>With Your Consent:</strong> We may share your information with your explicit consent.</li>
					</ul>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
					<p className="mb-4">
						We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">6. Your Rights and Choices</h2>
					<p className="mb-4">You have the right to:</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>Access:</strong> Request access to your personal information</li>
						<li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
						<li><strong>Deletion:</strong> Request deletion of your personal information (see section 7)</li>
						<li><strong>Objection:</strong> Object to processing of your personal information</li>
						<li><strong>Portability:</strong> Request transfer of your data</li>
						<li><strong>Withdraw Consent:</strong> Withdraw consent where we rely on consent to process your information</li>
					</ul>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">7. Data Deletion</h2>
					<p className="mb-4 font-semibold text-lg">
						You have the right to request deletion of all your personal information at any time.
					</p>
					<p className="mb-4">
						To request deletion of your data:
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>
							Use our{' '}
							<a href="/deleteaccount" className="font-semibold text-primary underline hover:no-underline">
								account &amp; data deletion
							</a>{' '}
							page, or contact us through support
						</li>
						<li>We will process your deletion request within 30 days</li>
						<li>Upon deletion, all your personal information will be permanently removed from our systems</li>
						<li>Some information may be retained as required by law or for legitimate business purposes (e.g., transaction records for accounting purposes)</li>
					</ul>
					<p className="mb-4">
						Please note that deleting your account will result in the permanent loss of all your data, including content you have created. This action cannot be undone.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">8. Cookies and Tracking Technologies</h2>
					<p className="mb-4">
						We use cookies and similar tracking technologies to track activity on our Service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">9. Children's Privacy</h2>
					<p className="mb-4">
						Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us to have that information removed.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">10. International Data Transfers</h2>
					<p className="mb-4">
						Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By using our Service, you consent to the transfer of your information to these facilities.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">11. Changes to This Privacy Policy</h2>
					<p className="mb-4">
						We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
					<p className="mb-4">
						If you have any questions about this Privacy Policy or wish to exercise your rights regarding your personal information, please contact us through our support channels.
					</p>
				</section>
			</div>
		</div>
	)
}
